import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useDeleteCommentMutation,
  useEditCommentMutation,
} from '@app/api/Client/api';
import { useUnpinClientCommentMutation } from '@app/api/ClientComments';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

// Свёрнутый комментарий показывает примерно три строки текста
// (font-size 13px, line-height 1.4).
const COLLAPSED_TEXT_MAX_HEIGHT = 56;
// Шаг наложения закреплённых комментариев: у более старых видна верхняя кромка.
const STICKY_STACK_STEP = 6;
// Глубже стопка не растёт, иначе закреплённые комментарии съедают высоту чата.
const STICKY_STACK_MAX_DEPTH = 3;
// Запас под шапку блока, отступы и сдвиг стопки при расчёте раскрытой высоты.
const EXPANDED_HEIGHT_RESERVE = 72;
const EXPANDED_MIN_HEIGHT = 96;

const COMMENT_OBJECT_TYPE = 'ClientComment';

interface UseLeadCommentMessageParams {
  commentId: number;
  commentText: string;
}

interface UseLeadCommentMessageResult {
  rootRef: (node: HTMLDivElement | null) => void;
  textRef: (node: HTMLElement | null) => void;
  isPinned: boolean;
  isUnpinning: boolean;
  unpin(): Promise<void>;
  stickyOffset: number;
  isExpanded: boolean;
  isExpandable: boolean;
  toggleExpanded(): void;
  collapsedMaxHeight: number;
  expandedMaxHeight?: number;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  draftText: string;
  setDraftText(value: string): void;
  startEditing(): void;
  saveDraft(): Promise<void>;
  deleteComment(): Promise<void>;
}

export const useLeadCommentMessage = ({
  commentId,
  commentText,
}: UseLeadCommentMessageParams): UseLeadCommentMessageResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();
  const {
    leadCardData,
    refetchLeadCard,
    refreshLatestHistory,
    removeHistoryItem,
    filteredLeadHistoryItems,
    scrollContainerRef,
  } = useLeadCardContext();

  const [editComment, { isLoading: isSaving }] = useEditCommentMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();
  const [unpinComment, { isLoading: isUnpinning }] =
    useUnpinClientCommentMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(commentText);

  const [rootNode, setRootNode] = useState<HTMLDivElement | null>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const [chatHeight, setChatHeight] = useState(0);
  const textObserverRef = useRef<ResizeObserver | null>(null);

  const rootRef = useCallback((node: HTMLDivElement | null) => {
    setRootNode(node);
  }, []);

  // Закрепление хранит бэк (us/11229): в GetLeadCard.ClientComments у
  // закреплённого комментария IsUnpinned = false. Закреплённые висят сверху
  // чата, остальные стоят на своём месте в истории.
  const pinnedCommentIds = useMemo(
    () =>
      new Set(
        (leadCardData?.ClientComments ?? [])
          .filter(({ IsUnpinned }) => !IsUnpinned)
          .map(({ Id }) => Id)
      ),
    [leadCardData?.ClientComments]
  );

  const isPinned = pinnedCommentIds.has(commentId);

  // Кнопка «Показать полностью» нужна только тем комментариям, которые не
  // помещаются в свёрнутую высоту. Полная высота текста берётся из
  // scrollHeight — он не зависит от текущего max-height блока.
  const textRef = useCallback((node: HTMLElement | null) => {
    textObserverRef.current?.disconnect();
    textObserverRef.current = null;

    if (!node) {
      setIsExpandable(false);
      return;
    }

    const updateExpandable = () => {
      setIsExpandable(node.scrollHeight > COLLAPSED_TEXT_MAX_HEIGHT + 1);
    };

    updateExpandable();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateExpandable);
    observer.observe(node);
    textObserverRef.current = observer;
  }, []);

  useEffect(
    () => () => {
      textObserverRef.current?.disconnect();
    },
    []
  );

  // Раскрытый комментарий не должен выходить за пределы чата, поэтому его
  // максимальная высота считается от высоты области скролла истории.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const updateChatHeight = () => setChatHeight(container.clientHeight);

    updateChatHeight();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateChatHeight);
    observer.observe(container);

    return () => observer.disconnect();
  }, [scrollContainerRef]);

  const stickyOffset = useMemo(() => {
    const pinnedIndex = filteredLeadHistoryItems
      .filter(
        ({ ObjectType, ObjectId }) =>
          ObjectType === COMMENT_OBJECT_TYPE && pinnedCommentIds.has(ObjectId)
      )
      .findIndex(({ ObjectId }) => ObjectId === commentId);

    if (pinnedIndex <= 0) return 0;

    return Math.min(pinnedIndex, STICKY_STACK_MAX_DEPTH) * STICKY_STACK_STEP;
  }, [commentId, filteredLeadHistoryItems, pinnedCommentIds]);

  const expandedMaxHeight = useMemo(() => {
    if (!chatHeight) return undefined;

    return Math.max(
      EXPANDED_MIN_HEIGHT,
      chatHeight - EXPANDED_HEIGHT_RESERVE - stickyOffset
    );
  }, [chatHeight, stickyOffset]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((previous) => !previous);
  }, []);

  const startEditing = useCallback(() => {
    setDraftText(commentText);
    setIsEditing(true);
  }, [commentText]);

  const unpin = useCallback(async () => {
    if (isUnpinning) return;

    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', 'frontWarning');
      return;
    }

    try {
      await unpinComment({ ActiveBranchId, commentId }).unwrap();

      // Признак закрепления живёт в карточке, поэтому после открепления
      // достаточно перезапросить её: комментарий останется строкой истории,
      // но перестанет висеть сверху чата.
      refetchLeadCard();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    commentId,
    isUnpinning,
    refetchLeadCard,
    showNotification,
    unpinComment,
  ]);

  const saveDraft = useCallback(async () => {
    if (isSaving) return;

    const trimmedText = draftText.trim();

    if (!trimmedText || trimmedText === commentText.trim()) {
      setIsEditing(false);
      setDraftText(commentText);
      return;
    }

    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', 'frontWarning');
      return;
    }

    try {
      await editComment({
        ActiveBranchId,
        commentId,
        text: trimmedText,
      }).unwrap();

      showNotification('Комментарий успешно изменён', 'success');
      setIsEditing(false);
      refetchLeadCard();
      void refreshLatestHistory();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    commentId,
    commentText,
    draftText,
    editComment,
    isSaving,
    refetchLeadCard,
    refreshLatestHistory,
    showNotification,
  ]);

  const deleteCommentHandler = useCallback(async () => {
    if (isDeleting) return;

    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', 'frontWarning');
      return;
    }

    try {
      await deleteComment({ ActiveBranchId, commentId }).unwrap();

      showNotification('Комментарий удалён', 'success');
      setIsEditing(false);
      // История склеивается мержем, поэтому удалённая строка сама из списка
      // не исчезнет — убираем её явно, а карточку и последние сообщения
      // обновляем теми же вызовами, что и при сохранении.
      removeHistoryItem(COMMENT_OBJECT_TYPE, commentId);
      refetchLeadCard();
      void refreshLatestHistory();
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    commentId,
    deleteComment,
    isDeleting,
    refetchLeadCard,
    refreshLatestHistory,
    removeHistoryItem,
    showNotification,
  ]);

  // Черновик сохраняется по клику ВНЕ карточки комментария и вне поповера
  // подтверждения удаления. Прежний onBlur у поля срабатывал и на кнопки
  // самого редактора: клик по «Сохранить» или по «Удалить» в поповере уводил
  // фокус, редактирование закрывалось раньше, чем отрабатывал сам клик
  // (замечание Михаила по issue/10992). Popconfirm живёт в портале, поэтому
  // проверка идёт по двум корням: узлу карточки и `.ant-popover`.
  useEffect(() => {
    if (!isEditing) return undefined;

    const saveOnOutsideClick = ({ target }: MouseEvent) => {
      if (!(target instanceof Element)) return;
      if (rootNode?.contains(target)) return;
      if (target.closest('.ant-popover')) return;

      void saveDraft();
    };

    document.addEventListener('mousedown', saveOnOutsideClick);

    return () => document.removeEventListener('mousedown', saveOnOutsideClick);
  }, [isEditing, rootNode, saveDraft]);

  return {
    rootRef,
    textRef,
    isPinned,
    isUnpinning,
    unpin,
    stickyOffset,
    isExpanded,
    isExpandable,
    toggleExpanded,
    collapsedMaxHeight: COLLAPSED_TEXT_MAX_HEIGHT,
    expandedMaxHeight,
    isEditing,
    isSaving,
    isDeleting,
    draftText,
    setDraftText,
    startEditing,
    saveDraft,
    deleteComment: deleteCommentHandler,
  };
};
